// frontend/src/components/ProjectView/ActorManagementSection.js
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { collection, deleteDoc, doc, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { getProjectAudienceActorsPath } from '../../utils/collectionPaths';
import { ActorService } from '@modules/firebase-core/services/ActorService';
import { ActorEvidenceDialog, InsightsFirestoreService } from '@modules/insights-analyzer';
// A função conductInterviewForActor (individual) não é mais usada aqui, pois a entrevista em lote é gerenciada em ProjectViewPage.

// MUI Imports
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Checkbox from '@mui/material/Checkbox'; // Para selecionar atores
// Corrected import
// Corrected import
// Para ver detalhes
// Added for select all checkbox label
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import TableSortLabel from '@mui/material/TableSortLabel';
import CircularProgress from '@mui/material/CircularProgress';

// Helper function para formatar Timestamp do Firestore ou Datas JS
const formatTimestamp = (timestampInput) => {
  if (!timestampInput) return 'N/A';
  let date;
  if (timestampInput.toDate) { // Objeto Timestamp do Firestore
    date = timestampInput.toDate();
  } else if (timestampInput instanceof Date) { // Objeto Date do JavaScript
    date = timestampInput;
  } else {
    try {
      date = new Date(timestampInput); // Tenta parsear se for string/número
      if (isNaN(date.getTime())) return 'Invalid Date';
    } catch {
      return 'Invalid Date';
    }
  }

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Mês é 0-indexado
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

// Helper function to convert array to comma-separated string
// const _arrayToCsv = (arr) => arr && Array.isArray(arr) ? arr.join(', ') : '';

// Helper function to convert comma-separated string to array
// const _csvToArray = (csv) => csv ? csv.split(',').map(item => item.trim()).filter(item => item) : [];

function ActorManagementSection({ 
    projectId, 
    audienceId,
    audienceName, 
    actors, 
    onActorsChange,
    currentUser,
    selectedActorsForInterview,
    onToggleActorSelection,
    isGlobalTaskRunning,
    onEditActor,
    onToggleSelectAllActorsInAudience,
    isActorBusy,
    getActorOperation,
    startActorDeletion,
    endActorDeletion,
    highlightActorId
}) {
  const [error, setError] = useState('');
  // const { showNotification } = useNotification();
  const [viewingActorDetails, setViewingActorDetails] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuActorId, setMenuActorId] = useState(null);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  // Spec C (C2): local weight override so the stepper feels instant while the
  // managed write + parent refresh round-trips.
  const [pendingWeights, setPendingWeights] = useState({});
  // Spec D3: actor evidence view (Layers A/B) against the newest completed analysis.
  const [evidenceActor, setEvidenceActor] = useState(null);
  const [evidenceAnalysisId, setEvidenceAnalysisId] = useState(null);

  const handleOpenEvidence = async (actor) => {
    setError('');
    try {
      const docs = await new InsightsFirestoreService(projectId).list({ limit: 10 });
      const newest = (docs || [])
        .filter(d => d.status === 'completed' || d.status === 'partial')
        .sort((a, b) => String(b.createdAt ?? '').localeCompare(String(a.createdAt ?? '')))[0];
      if (!newest) {
        setError('No completed insights analysis yet — generate one first (Insights tab).');
        return;
      }
      setEvidenceAnalysisId(newest.id);
      setEvidenceActor(actor);
    } catch (err) {
      setError(err.message || 'Failed to resolve the newest insights analysis');
    }
  };
  // const [, _setDeletingActorId] = useState(null); // Unused state variable

  // Spec C (C2, AC-C2-4): weight edits go through the managed firebase-core
  // path (ActorService.update), never a raw scattered Firestore write.
  const handleWeightStep = async (actor, delta) => {
    const current = pendingWeights[actor.id] ?? (Number(actor.weight) || 1);
    const next = Math.min(5, Math.max(1, current + delta));
    if (next === current) return;
    setPendingWeights(prev => ({ ...prev, [actor.id]: next }));
    setError('');
    try {
      await new ActorService(projectId, audienceId).update(actor.id, { weight: next });
      if (onActorsChange) onActorsChange();
    } catch (err) {
      setPendingWeights(prev => ({ ...prev, [actor.id]: current }));
      setError(`Failed to update actor weight: ${err.message}`);
    }
  };

  const handleDeleteActor = async (actorId) => {
    if (!currentUser || !window.confirm("Are you sure you want to delete this actor and all its data?")) return;
    setError('');
    
    // Start deletion tracking
    startActorDeletion([actorId]);
    
    try {
      // Define the subcollections that actors have
      const subcollections = ['coreJobs', 'contexts', 'fears', 'bigJobs', 'subtasks', 'competitors', 'habits'];
      
      // Delete each subcollection
      for (const subcollectionName of subcollections) {
        const subcollectionRef = collection(db, `projects/${projectId}/audiences/${audienceId}/actors/${actorId}/${subcollectionName}`);
        const subcollectionSnapshot = await getDocs(subcollectionRef);
        
        // Delete all documents in the subcollection
        for (const subDoc of subcollectionSnapshot.docs) {
          await deleteDoc(subDoc.ref);
        }
      }
      
      // Delete the actor document itself
      await deleteDoc(doc(db, getProjectAudienceActorsPath(projectId, audienceId), actorId));
      
      if (onActorsChange) onActorsChange();
      // showNotification('Actor and all its data deleted successfully!', 'success');
    } catch (err) {
      setError(`Failed to delete actor: ${err.message}`);
      console.error("Error deleting actor:", err);
    } finally {
      // End deletion tracking
      endActorDeletion([actorId]);
    }
  };

  // Determine if all actors in this specific audience are selected
  const areAllInAudienceSelected = actors.length > 0 && actors.every(actor => selectedActorsForInterview?.has(actor.id));

  const handleMenuOpen = (event, actorId) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuActorId(actorId);
  };
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuActorId(null);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const sortedActors = React.useMemo(() => {
    const sorted = [...actors];
    sorted.sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];
      if (sortBy === 'creationDate') {
        aValue = aValue ? new Date(aValue) : new Date(0);
        bValue = bValue ? new Date(bValue) : new Date(0);
      } else if (Array.isArray(aValue) && Array.isArray(bValue)) {
        aValue = aValue[0] || '';
        bValue = bValue[0] || '';
      } else {
        aValue = aValue || '';
        bValue = bValue || '';
      }
      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [actors, sortBy, sortOrder]);

  return (
    <Box sx={{width: '100%'}}>
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }} 
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}
      
      {/* Removed Dialog para Adicionar/Editar Ator */}

      {/* Dialog para Ver Detalhes Completos do Ator */}
      <Dialog
        open={Boolean(viewingActorDetails)}
        onClose={() => setViewingActorDetails(null)}
        maxWidth="md"
        fullWidth
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.15)',
              backdropFilter: 'blur(20px)',
            }
          }
        }}
      >
        <DialogTitle>
          Actor Details: {viewingActorDetails?.name} 
          <Typography variant="caption" color="textSecondary" sx={{ml:1}}> (Audience: {audienceName})</Typography>
        </DialogTitle>
        <DialogContent dividers>
          {viewingActorDetails && (
            <Box sx={{'& > *': {mb: 1.5}}}>
              <Typography><strong>Description:</strong> {viewingActorDetails.description || "N/A"}</Typography>
              <Typography variant="body2" color="textSecondary">Created: {formatTimestamp(viewingActorDetails.creationDate)}</Typography>
              <hr style={{margin: '12px 0'}}/>
              
              {['coreJobs', 'contexts', 'fears', 'bigJobs', 'subtasks', 'competitors', 'habits'].map((field) => (
                <Box key={field} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ textTransform: 'capitalize' }}>
                    {field.replace(/([A-Z])/g, ' $1').trim()}:
                  </Typography>
                  {Array.isArray(viewingActorDetails[field]) && viewingActorDetails[field].length > 0 ? (
                    <Box component="ul" sx={{ pl: 2, mt: 0.5 }}>
                      {viewingActorDetails[field].map((item, idx) => (
                        <Box key={idx} component="li" sx={{ mb: 1 }}>
                          <Typography variant="body2">
                            <strong>Description:</strong> {typeof item === 'string' ? item : item.description}
                          </Typography>
                          {typeof item === 'object' && (
                            <>
                              <Typography variant="body2">
                                <strong>Full Phrase:</strong> {item.fullPhraseFromInterview}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Weight:</strong> {item.weight}
                              </Typography>
                            </>
                          )}
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="textSecondary" sx={{ pl: 2 }}>None</Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewingActorDetails(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {(!actors || actors.length === 0) && (
        <Typography sx={{ textAlign: 'start', fontStyle: 'italic', color: 'text.secondary' }}>
          No actors created yet for &quot;{audienceName}&quot;.
        </Typography>
      )}
      
      {actors && actors.length > 0 && (
        <TableContainer component={Paper} elevation={0} variant="outlined" sx={{mt: 1, overflowX: 'auto'}}>
          <Table size="small" sx={{minWidth: 1200}}> {/* minWidth to encourage horizontal scroll */}
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ verticalAlign: 'middle', textAlign: 'center', width: 48 }}>
                  <Checkbox
                    indeterminate={actors.length > 0 && !areAllInAudienceSelected && actors.some(actor => selectedActorsForInterview?.has(actor.id))}
                    checked={areAllInAudienceSelected}
                    onChange={() => onToggleSelectAllActorsInAudience(actors)}
                    disabled={isGlobalTaskRunning || actors.length === 0 || actors.some(actor => isActorBusy(actor.id))}
                    size="small"
                    color="primary"
                  />
                </TableCell>
                <TableCell sx={{minWidth: 150, whiteSpace: 'normal', wordBreak: 'break-word'}}>
                  <TableSortLabel
                    active={sortBy === 'name'}
                    direction={sortBy === 'name' ? sortOrder : 'asc'}
                    onClick={() => handleSort('name')}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                {/* <TableCell sx={{minWidth: 200, whiteSpace: 'normal', wordBreak: 'break-word'}}>Description</TableCell> */}
                <TableCell sx={{minWidth: 150, whiteSpace: 'normal', wordBreak: 'break-word'}}>
                  <TableSortLabel
                    active={sortBy === 'coreJobs'}
                    direction={sortBy === 'coreJobs' ? sortOrder : 'asc'}
                    onClick={() => handleSort('coreJobs')}
                  >
                    Core Jobs
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{minWidth: 150, whiteSpace: 'normal', wordBreak: 'break-word'}}>
                  <TableSortLabel
                    active={sortBy === 'contexts'}
                    direction={sortBy === 'contexts' ? sortOrder : 'asc'}
                    onClick={() => handleSort('contexts')}
                  >
                    Contexts
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{minWidth: 150, whiteSpace: 'normal', wordBreak: 'break-word'}}>
                  <TableSortLabel
                    active={sortBy === 'fears'}
                    direction={sortBy === 'fears' ? sortOrder : 'asc'}
                    onClick={() => handleSort('fears')}
                  >
                    Fears
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{minWidth: 150, whiteSpace: 'normal', wordBreak: 'break-word'}}>
                  <TableSortLabel
                    active={sortBy === 'bigJobs'}
                    direction={sortBy === 'bigJobs' ? sortOrder : 'asc'}
                    onClick={() => handleSort('bigJobs')}
                  >
                    Big Jobs
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{minWidth: 150, whiteSpace: 'normal', wordBreak: 'break-word'}}>
                  <TableSortLabel
                    active={sortBy === 'subtasks'}
                    direction={sortBy === 'subtasks' ? sortOrder : 'asc'}
                    onClick={() => handleSort('subtasks')}
                  >
                    Subtasks
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{minWidth: 150, whiteSpace: 'normal', wordBreak: 'break-word'}}>
                  <TableSortLabel
                    active={sortBy === 'competitors'}
                    direction={sortBy === 'competitors' ? sortOrder : 'asc'}
                    onClick={() => handleSort('competitors')}
                  >
                    Competitors
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{minWidth: 150, whiteSpace: 'normal', wordBreak: 'break-word'}}>
                  <TableSortLabel
                    active={sortBy === 'habits'}
                    direction={sortBy === 'habits' ? sortOrder : 'asc'}
                    onClick={() => handleSort('habits')}
                  >
                    Habits
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{minWidth: 100, whiteSpace: 'nowrap'}}>
                  <TableSortLabel
                    active={sortBy === 'weight'}
                    direction={sortBy === 'weight' ? sortOrder : 'asc'}
                    onClick={() => handleSort('weight')}
                  >
                    Weight
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{minWidth: 120, whiteSpace: 'normal', wordBreak: 'break-word'}}>
                  <TableSortLabel
                    active={sortBy === 'creationDate'}
                    direction={sortBy === 'creationDate' ? sortOrder : 'asc'}
                    onClick={() => handleSort('creationDate')}
                  >
                    Created
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{minWidth: 150, whiteSpace: 'normal', wordBreak: 'break-word'}}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedActors.map((actor) => (
                <TableRow
                  hover
                  key={actor.id}
                  data-testid={`actor-row-${actor.id}`}
                  selected={selectedActorsForInterview?.has(actor.id)}
                  style={{ cursor: 'pointer' }}
                  ref={actor.id === highlightActorId ? (node) => {
                    if (node) setTimeout(() => node.scrollIntoView({ behavior: 'smooth', block: 'center' }), 150);
                  } : undefined}
                  sx={actor.id === highlightActorId ? { backgroundColor: 'action.selected', outline: '2px solid', outlineColor: 'primary.main' } : undefined}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      checked={selectedActorsForInterview?.has(actor.id) || false}
                      onChange={() => onToggleActorSelection(actor, audienceName)}
                      disabled={isGlobalTaskRunning || isActorBusy(actor.id)}
                      onClick={e => e.stopPropagation()}
                      size="small"
                    />
                  </TableCell>
                  <TableCell component="th" scope="row" sx={{whiteSpace: 'normal', wordBreak: 'break-word'}} onClick={() => onEditActor(actor)} style={{ cursor: 'pointer' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      <span>{actor.name}</span>
                      {actor.role ? (
                        <Chip
                          label={actor.role}
                          size="small"
                          color="primary"
                          variant="outlined"
                          title={actor.influence || `DMU role: ${actor.role}`}
                          sx={{ textTransform: 'capitalize', fontSize: '0.7rem', height: 20 }}
                        />
                      ) : null}
                    </Box>
                  </TableCell>
                  {/* <TableCell sx={{whiteSpace: 'normal', wordBreak: 'break-word'}}>{actor.description?.substring(0,50)}{actor.description?.length > 50 ? '...' : ''}</TableCell> */}
                  <TableCell sx={{whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis'}} onClick={() => onEditActor(actor)} style={{ cursor: 'pointer' }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {Array.isArray(actor.coreJobs) && actor.coreJobs.length > 0
                        ? actor.coreJobs.map((item, idx) => (
                            <Chip
                              key={idx}
                              label={typeof item === 'string' ? item : item.description}
                              size="small"
                            />
                          ))
                        : 'None'}
                    </Box>
                  </TableCell>
                  <TableCell sx={{whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis'}} onClick={() => onEditActor(actor)} style={{ cursor: 'pointer' }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {Array.isArray(actor.contexts) && actor.contexts.length > 0
                        ? actor.contexts.map((item, idx) => (
                            <Chip
                              key={idx}
                              label={typeof item === 'string' ? item : item.description}
                              size="small"
                            />
                          ))
                        : 'None'}
                    </Box>
                  </TableCell>
                  <TableCell sx={{whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis'}} onClick={() => onEditActor(actor)} style={{ cursor: 'pointer' }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {Array.isArray(actor.fears) && actor.fears.length > 0
                        ? actor.fears.map((item, idx) => (
                            <Chip
                              key={idx}
                              label={typeof item === 'string' ? item : item.description}
                              size="small"
                            />
                          ))
                        : 'None'}
                    </Box>
                  </TableCell>
                  <TableCell sx={{whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis'}} onClick={() => onEditActor(actor)} style={{ cursor: 'pointer' }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {Array.isArray(actor.bigJobs) && actor.bigJobs.length > 0
                        ? actor.bigJobs.map((item, idx) => (
                            <Chip
                              key={idx}
                              label={typeof item === 'string' ? item : item.description}
                              size="small"
                            />
                          ))
                        : 'None'}
                    </Box>
                  </TableCell>
                  <TableCell sx={{whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis'}} onClick={() => onEditActor(actor)} style={{ cursor: 'pointer' }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {Array.isArray(actor.subtasks) && actor.subtasks.length > 0
                        ? actor.subtasks.map((item, idx) => (
                            <Chip
                              key={idx}
                              label={typeof item === 'string' ? item : item.description}
                              size="small"
                            />
                          ))
                        : 'None'}
                    </Box>
                  </TableCell>
                  <TableCell sx={{whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis'}} onClick={() => onEditActor(actor)} style={{ cursor: 'pointer' }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {Array.isArray(actor.competitors) && actor.competitors.length > 0
                        ? actor.competitors.map((item, idx) => (
                            <Chip
                              key={idx}
                              label={typeof item === 'string' ? item : item.description}
                              size="small"
                            />
                          ))
                        : 'None'}
                    </Box>
                  </TableCell>
                  <TableCell sx={{whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis'}} onClick={() => onEditActor(actor)} style={{ cursor: 'pointer' }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {Array.isArray(actor.habits) && actor.habits.length > 0
                        ? actor.habits.map((item, idx) => (
                            <Chip
                              key={idx}
                              label={typeof item === 'string' ? item : item.description}
                              size="small"
                            />
                          ))
                        : 'None'}
                    </Box>
                  </TableCell>
                  <TableCell sx={{whiteSpace: 'nowrap'}} data-testid={`actor-weight-stepper-${actor.id}`} data-audience-id={audienceId}>
                    {/* Spec C (C2): inline 1-5 weight stepper via the managed path. */}
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }} onClick={e => e.stopPropagation()}>
                      <IconButton
                        size="small"
                        data-testid={`actor-weight-dec-${actor.id}`}
                        disabled={isGlobalTaskRunning || isActorBusy(actor.id) || (pendingWeights[actor.id] ?? (Number(actor.weight) || 1)) <= 1}
                        onClick={() => handleWeightStep(actor, -1)}
                        aria-label="decrease weight"
                        sx={{ p: 0.25 }}
                      >
                        <Typography variant="body2" component="span">−</Typography>
                      </IconButton>
                      <Typography variant="body2" component="span" data-testid={`actor-weight-value-${actor.id}`} sx={{ minWidth: 14, textAlign: 'center' }}>
                        {pendingWeights[actor.id] ?? (actor.weight || 1)}
                      </Typography>
                      <IconButton
                        size="small"
                        data-testid={`actor-weight-inc-${actor.id}`}
                        disabled={isGlobalTaskRunning || isActorBusy(actor.id) || (pendingWeights[actor.id] ?? (Number(actor.weight) || 1)) >= 5}
                        onClick={() => handleWeightStep(actor, 1)}
                        aria-label="increase weight"
                        sx={{ p: 0.25 }}
                      >
                        <Typography variant="body2" component="span">+</Typography>
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell sx={{whiteSpace: 'normal', wordBreak: 'break-word'}} onClick={() => onEditActor(actor)} style={{ cursor: 'pointer' }}>{formatTimestamp(actor.creationDate)}</TableCell>
                  <TableCell align="right" sx={{whiteSpace: 'nowrap'}}>
                    <IconButton
                      size="small"
                      onClick={e => handleMenuOpen(e, actor.id)}
                      aria-label="actions"
                      aria-controls={menuActorId === actor.id ? `actor-menu-${actor.id}` : undefined}
                      aria-haspopup="true"
                      disabled={isActorBusy(actor.id)}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                    <Menu
                      id={`actor-menu-${actor.id}`}
                      anchorEl={menuAnchorEl}
                      open={Boolean(menuAnchorEl) && menuActorId === actor.id}
                      onClose={handleMenuClose}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                      PaperProps={{ sx: { minWidth: 160 } }}
                    >
                      <MenuItem onClick={() => { setViewingActorDetails(actor); handleMenuClose(); }}>View Details</MenuItem>
                      <MenuItem
                        data-testid={`actor-evidence-open-${actor.id}`}
                        onClick={() => { handleMenuClose(); handleOpenEvidence(actor); }}
                      >
                        Evidence & what-if
                      </MenuItem>
                      <MenuItem onClick={() => { onEditActor(actor); handleMenuClose(); }}>Edit</MenuItem>
                      <MenuItem 
                        onClick={() => { handleDeleteActor(actor.id); handleMenuClose(); }}
                        disabled={isActorBusy(actor.id)}
                      >
                        {isActorBusy(actor.id) ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={16} />
                            {(() => {
                              const operation = getActorOperation(actor.id);
                              if (operation?.operation === 'interview') return 'Interviewing...';
                              if (operation?.operation === 'delete') return 'Deleting...';
                              return 'Processing...';
                            })()}
                          </Box>
                        ) : (
                          'Delete'
                        )}
                      </MenuItem>
                    </Menu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {/* Spec D3: Layers A/B actor evidence view against the newest analysis. */}
      {evidenceActor && evidenceAnalysisId && (
        <ActorEvidenceDialog
          open
          onClose={() => setEvidenceActor(null)}
          projectId={projectId}
          analysisId={evidenceAnalysisId}
          actorId={evidenceActor.id}
          actorName={evidenceActor.name}
        />
      )}
    </Box>
  );
}

ActorManagementSection.propTypes = {
  projectId: PropTypes.string.isRequired,
  audienceId: PropTypes.string.isRequired,
  audienceName: PropTypes.string.isRequired,
  actors: PropTypes.array.isRequired,
  onActorsChange: PropTypes.func,
  currentUser: PropTypes.object.isRequired,
  selectedActorsForInterview: PropTypes.object,
  onToggleActorSelection: PropTypes.func.isRequired,
  isGlobalTaskRunning: PropTypes.bool,
  onEditActor: PropTypes.func.isRequired,
  onToggleSelectAllActorsInAudience: PropTypes.func.isRequired,
  isActorBusy: PropTypes.func.isRequired,
  getActorOperation: PropTypes.func.isRequired,
  startActorDeletion: PropTypes.func.isRequired,
  endActorDeletion: PropTypes.func.isRequired,
  highlightActorId: PropTypes.string,
};

export default ActorManagementSection;